import { useState, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    PiArrowDownRightBold,
    PiArrowUpRightBold,
    PiCurrencyDollarBold,
    PiSealWarningBold,
    PiTrophyBold,
    PiUsersThreeBold,
    PiWarningCircleBold,
    PiCalendarBlankBold,
    PiListChecksBold,
} from 'react-icons/pi';
import { Button, PageHeader, LoadingState, Text, Title } from '@v2/design-system';
import TablePagination, { TABLE_PER_PAGE_OPTIONS } from '@v2/components/TablePagination';
import { cn } from '@v2/utils/cn';
import api from '@v2/lib/api';
import { isSuperAdmin } from '@v2/lib/auth';
import toast from 'react-hot-toast';

type ExceptionAuditRow = {
    id: number;
    deal_id: number;
    deal_number: number | string | null;
    deal_title: string | null;
    deal_amount: number | null;
    seller: { id: number; name: string; email: string } | null;
    expected_commission: number;
    manual_commission: number;
    calculated_commission: number;
    due_commission: number;
    calculated_rate_pp: number | null;
    due_rate_pp: number | null;
    minimum_rate_pp: number;
    difference: number;
    commission_floor: boolean;
    strike_points: number;
    exception: boolean;
    status: string;
    created_by: string | null;
    created_at: string | null;
};

type ExecutiveSummary = {
    month_label: string;
    filtered?: boolean;
    strikes_total: number;
    strikes_this_month: number;
    strikes_last_month: number;
    strikes_mom_delta: number;
    uplift_total: number;
    uplift_this_month: number;
    open_audits: number;
    exception_count: number;
    floor_count: number;
    sellers_with_strikes: number;
    top_seller: { id: number | null; name: string; strikes: number; deals_count: number } | null;
    top_team: { team_id: number | null; team_name: string; strikes: number; sellers: number } | null;
    teams: Array<{ team_id: number | null; team_name: string; strikes: number; sellers: number }>;
    top_sellers: Array<{ id: number | null; name: string; strikes: number; deals_count: number }>;
};

type PillTone = 'gray' | 'amber' | 'emerald' | 'rose' | 'sky' | 'orange';

const PILL_TONES: Record<PillTone, string> = {
    gray: 'bg-gray-100 text-gray-700 ring-gray-200',
    amber: 'bg-amber-100 text-amber-800 ring-amber-200',
    emerald: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
    rose: 'bg-rose-100 text-rose-800 ring-rose-200',
    sky: 'bg-sky-100 text-sky-800 ring-sky-200',
    orange: 'bg-orange-100 text-orange-800 ring-orange-200',
};

function Pill({ tone, children, className }: { tone: PillTone; children: ReactNode; className?: string }) {
    return (
        <span
            className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset',
                PILL_TONES[tone],
                className,
            )}
        >
            {children}
        </span>
    );
}

function formatBrl(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function formatPct(value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    return `${new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value)}%`;
}

function ExecKpi({
    label,
    value,
    sub,
    icon: Icon,
    color,
}: {
    label: string;
    value: string;
    sub?: string;
    icon: React.ElementType;
    color: 'orange' | 'amber' | 'emerald' | 'rose' | 'sky' | 'violet';
}) {
    const tones = {
        orange: {
            card: 'border-orange-100 bg-gradient-to-br from-orange-50 to-white',
            icon: 'bg-orange-100 text-orange-700',
            value: 'text-orange-950',
        },
        amber: {
            card: 'border-amber-100 bg-gradient-to-br from-amber-50 to-white',
            icon: 'bg-amber-100 text-amber-700',
            value: 'text-amber-950',
        },
        emerald: {
            card: 'border-emerald-100 bg-gradient-to-br from-emerald-50 to-white',
            icon: 'bg-emerald-100 text-emerald-700',
            value: 'text-emerald-950',
        },
        rose: {
            card: 'border-rose-100 bg-gradient-to-br from-rose-50 to-white',
            icon: 'bg-rose-100 text-rose-700',
            value: 'text-rose-950',
        },
        sky: {
            card: 'border-sky-100 bg-gradient-to-br from-sky-50 to-white',
            icon: 'bg-sky-100 text-sky-700',
            value: 'text-sky-950',
        },
        violet: {
            card: 'border-violet-100 bg-gradient-to-br from-violet-50 to-white',
            icon: 'bg-violet-100 text-violet-700',
            value: 'text-violet-950',
        },
    }[color];

    return (
        <div className={cn('rounded-2xl border p-4 shadow-sm', tones.card)}>
            <div className="mb-3 flex items-start justify-between gap-2">
                <div className={cn('flex h-9 w-9 items-center justify-center rounded-xl', tones.icon)}>
                    <Icon className="size-5" />
                </div>
            </div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
            <p className={cn('mt-1 text-2xl font-bold tracking-tight', tones.value)}>{value}</p>
            {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
        </div>
    );
}

export default function CommissionExceptionAuditPage() {
    const qc = useQueryClient();
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        commission_floor_only: false,
        strikes_only: false,
        seller_user_id: '',
        from: '',
        to: '',
        page: 1,
        per_page: 25,
    });

    const allowed = isSuperAdmin();

    const { data, isLoading } = useQuery({
        queryKey: ['commissioning', 'exception-audits', filters],
        queryFn: () =>
            api
                .get('/commissioning/exception-audits', {
                    params: {
                        ...filters,
                        seller_user_id: filters.seller_user_id || undefined,
                        commission_floor_only: filters.commission_floor_only ? 1 : undefined,
                        strikes_only: filters.strikes_only ? 1 : undefined,
                    },
                })
                .then((r) => r.data),
        placeholderData: (prev) => prev,
        enabled: allowed,
    });

    const syncMutation = useMutation({
        mutationFn: () => api.post('/commissioning/exception-audits/sync-floors', { limit: 300 }),
        onSuccess: (res) => {
            toast.success(`Strikes sincronizados: ${res.data.synced} deal(s)`);
            qc.invalidateQueries({ queryKey: ['commissioning', 'exception-audits'] });
        },
        onError: () => toast.error('Falha ao sincronizar travas 1%'),
    });

    if (!allowed) {
        return <Navigate to="/forbidden" replace />;
    }

    const rows: ExceptionAuditRow[] = data?.data ?? [];
    const exec: ExecutiveSummary | null = data?.executive ?? null;
    const meta = data?.meta;
    const momDelta = exec?.strikes_mom_delta ?? 0;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Auditoria de Exceções"
                subtitle="Visão executiva de travas 1%, strikes e exceções Ploomes"
            >
                <Button
                    size="sm"
                    variant="outline"
                    isLoading={syncMutation.isPending}
                    onClick={() => syncMutation.mutate()}
                >
                    Sincronizar travas 1%
                </Button>
            </PageHeader>

            <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <Text className="text-xs text-gray-500">
                        Filtros valem para <span className="font-semibold text-gray-700">KPIs, rankings e tabela</span>
                        {exec?.filtered ? (
                            <span className="ml-2 inline-flex">
                                <Pill tone="sky">filtrado</Pill>
                            </span>
                        ) : null}
                    </Text>
                    {(filters.from || filters.to || filters.search || filters.status || filters.commission_floor_only || filters.strikes_only || filters.seller_user_id) && (
                        <button
                            type="button"
                            className="text-xs font-medium text-gray-600 hover:underline"
                            onClick={() =>
                                setFilters({
                                    search: '',
                                    status: '',
                                    commission_floor_only: false,
                                    strikes_only: false,
                                    seller_user_id: '',
                                    from: '',
                                    to: '',
                                    page: 1,
                                    per_page: filters.per_page,
                                })
                            }
                        >
                            Limpar filtros
                        </button>
                    )}
                </div>
                <div className="flex flex-wrap gap-3">
                    <input
                        type="search"
                        placeholder="Buscar deal / vendedor…"
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        value={filters.search}
                        onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value, page: 1 }))}
                    />
                    <select
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        value={filters.status}
                        onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value, page: 1 }))}
                    >
                        <option value="">Todos os status</option>
                        <option value="open">Aberto</option>
                        <option value="reviewed">Revisado</option>
                    </select>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={filters.commission_floor_only}
                            onChange={(e) =>
                                setFilters((p) => ({ ...p, commission_floor_only: e.target.checked, page: 1 }))
                            }
                        />
                        Só trava 1%
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                            type="checkbox"
                            checked={filters.strikes_only}
                            onChange={(e) =>
                                setFilters((p) => ({ ...p, strikes_only: e.target.checked, page: 1 }))
                            }
                        />
                        Só strikes
                    </label>
                    <input
                        type="date"
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        value={filters.from}
                        onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value, page: 1 }))}
                        title="De"
                    />
                    <input
                        type="date"
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        value={filters.to}
                        onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value, page: 1 }))}
                        title="Até"
                    />
                    {meta && <span className="self-center ml-auto text-sm text-gray-500">{meta.total} na tabela</span>}
                </div>
            </div>

            {/* KPI executivo */}
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <ExecKpi
                    label={filters.from || filters.to ? `Strikes — período` : `Strikes — ${exec?.month_label ?? 'mês'}`}
                    value={String(exec?.strikes_this_month ?? 0)}
                    sub={
                        filters.from || filters.to
                            ? `Comparativo janela anterior: ${exec?.strikes_last_month ?? 0}`
                            : momDelta === 0
                              ? `vs mês anterior: ${exec?.strikes_last_month ?? 0}`
                              : `${momDelta > 0 ? '+' : ''}${momDelta} vs mês anterior (${exec?.strikes_last_month ?? 0})`
                    }
                    icon={PiCalendarBlankBold}
                    color="orange"
                />
                <ExecKpi
                    label="Top strike"
                    value={exec?.top_seller?.name ?? '—'}
                    sub={exec?.top_seller ? `${exec.top_seller.strikes} strike(s) · ${exec.top_seller.deals_count} venda(s)` : 'Sem strikes'}
                    icon={PiTrophyBold}
                    color="rose"
                />
                <ExecKpi
                    label="Equipe com mais strikes"
                    value={exec?.top_team?.team_name ?? '—'}
                    sub={exec?.top_team ? `${exec.top_team.strikes} strike(s) · ${exec.top_team.sellers} vendedor(es)` : 'Sem equipe'}
                    icon={PiUsersThreeBold}
                    color="violet"
                />
                <ExecKpi
                    label={filters.from || filters.to ? 'Uplift da trava (período)' : 'Uplift da trava (mês)'}
                    value={formatBrl(exec?.uplift_this_month ?? 0)}
                    sub={`No filtro: ${formatBrl(exec?.uplift_total ?? 0)}`}
                    icon={PiCurrencyDollarBold}
                    color="emerald"
                />
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <ExecKpi
                    label="Strikes no filtro"
                    value={String(exec?.strikes_total ?? 0)}
                    sub={`${exec?.sellers_with_strikes ?? 0} vendedor(es) com strike`}
                    icon={PiSealWarningBold}
                    color="amber"
                />
                <ExecKpi
                    label="Travas 1% registradas"
                    value={String(exec?.floor_count ?? 0)}
                    sub="Vendas abaixo do mínimo"
                    icon={PiWarningCircleBold}
                    color="orange"
                />
                <ExecKpi
                    label="Exceções Ploomes"
                    value={String(exec?.exception_count ?? 0)}
                    sub="% manual vs regra Hub"
                    icon={PiListChecksBold}
                    color="sky"
                />
                <ExecKpi
                    label="Auditorias abertas"
                    value={String(exec?.open_audits ?? 0)}
                    sub="Pendentes de revisão"
                    icon={momDelta > 0 ? PiArrowUpRightBold : PiArrowDownRightBold}
                    color="rose"
                />
            </div>

            {/* Rankings */}
            <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                    <Title as="h3" className="text-sm font-semibold text-gray-900">Top vendedores (strikes)</Title>
                    <Text className="mb-3 text-xs text-gray-500">Quem mais acionou a trava mínima</Text>
                    {(exec?.top_sellers?.length ?? 0) === 0 ? (
                        <p className="text-sm text-gray-500">Nenhum strike ainda.</p>
                    ) : (
                        <ul className="space-y-2">
                            {(exec?.top_sellers ?? []).map((s, idx) => (
                                <li key={s.id ?? s.name} className="flex items-center justify-between gap-2 rounded-xl border border-gray-100 px-3 py-2">
                                    <div className="flex min-w-0 items-center gap-2">
                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-100 text-xs font-bold text-rose-700">
                                            {idx + 1}
                                        </span>
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-semibold text-gray-900">{s.name}</p>
                                            <p className="text-[11px] text-gray-500">{s.deals_count} venda(s)</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setFilters((p) => ({
                                                ...p,
                                                seller_user_id: String(s.id ?? ''),
                                                strikes_only: true,
                                                page: 1,
                                            }))
                                        }
                                    >
                                        <Pill tone="rose">{s.strikes} strike{s.strikes === 1 ? '' : 's'}</Pill>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                    <Title as="h3" className="text-sm font-semibold text-gray-900">Top equipes (strikes)</Title>
                    <Text className="mb-3 text-xs text-gray-500">Concentração de travas por squad</Text>
                    {(exec?.teams?.length ?? 0) === 0 ? (
                        <p className="text-sm text-gray-500">Nenhuma equipe com strike.</p>
                    ) : (
                        <ul className="space-y-2">
                            {(exec?.teams ?? []).map((t, idx) => (
                                <li key={t.team_id ?? t.team_name} className="flex items-center justify-between gap-2 rounded-xl border border-gray-100 px-3 py-2">
                                    <div className="flex min-w-0 items-center gap-2">
                                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">
                                            {idx + 1}
                                        </span>
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-semibold text-gray-900">{t.team_name}</p>
                                            <p className="text-[11px] text-gray-500">{t.sellers} vendedor(es)</p>
                                        </div>
                                    </div>
                                    <Pill tone="orange">{t.strikes} strike{t.strikes === 1 ? '' : 's'}</Pill>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {isLoading && <LoadingState variant="table" />}

            <div className="overflow-hidden rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                        <tr>
                            <th className="px-4 py-3 text-left font-medium">Data</th>
                            <th className="px-4 py-3 text-left font-medium">Deal</th>
                            <th className="px-4 py-3 text-left font-medium">Vendedor</th>
                            <th className="px-4 py-3 text-right font-medium">
                                <span className="block">Antes da trava</span>
                                <span className="block text-[10px] font-normal text-gray-400">regra / calculado</span>
                            </th>
                            <th className="px-4 py-3 text-right font-medium">
                                <span className="block">Comissão devida</span>
                                <span className="block text-[10px] font-normal text-gray-400">o que deve receber</span>
                            </th>
                            <th className="px-4 py-3 text-right font-medium">Diferença</th>
                            <th className="px-4 py-3 text-left font-medium">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {rows.map((row) => {
                            const calculated = row.calculated_commission ?? row.expected_commission;
                            const due = row.due_commission ?? row.manual_commission;

                            return (
                                <tr key={row.id} className="hover:bg-gray-50/80">
                                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap align-top">
                                        {row.created_at ?? '—'}
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        <Link
                                            to={`/ploomes-deals/${row.deal_id}`}
                                            className="font-medium text-blue-700 hover:underline"
                                        >
                                            #{row.deal_number ?? row.deal_id}
                                        </Link>
                                        {row.deal_title && (
                                            <p className="max-w-xs truncate text-xs text-gray-500">{row.deal_title}</p>
                                        )}
                                        {row.deal_amount != null && (
                                            <div className="mt-1.5">
                                                <Pill tone="gray">Venda {formatBrl(row.deal_amount)}</Pill>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 align-top">{row.seller?.name ?? '—'}</td>
                                    <td className="px-4 py-3 text-right align-top">
                                        <p className={`font-medium ${row.commission_floor ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                                            {formatBrl(calculated)}
                                        </p>
                                        <div className="mt-1.5 flex flex-wrap justify-end gap-1">
                                            {row.calculated_rate_pp != null && (
                                                <Pill tone="gray">{formatPct(row.calculated_rate_pp)}</Pill>
                                            )}
                                            {row.commission_floor && (
                                                <Pill tone="amber">abaixo do mínimo</Pill>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right align-top">
                                        <p className="text-base font-bold text-emerald-700">{formatBrl(due)}</p>
                                        <div className="mt-1.5 flex flex-wrap justify-end gap-1">
                                            {row.due_rate_pp != null && (
                                                <Pill tone={row.commission_floor ? 'emerald' : row.exception ? 'amber' : 'sky'}>
                                                    {formatPct(row.due_rate_pp)}
                                                    {row.commission_floor ? ' mínimo' : row.exception ? ' manual' : ''}
                                                </Pill>
                                            )}
                                            {row.commission_floor && (
                                                <Pill tone="emerald">trava {formatPct(row.minimum_rate_pp)}</Pill>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right align-top">
                                        <p className={`font-semibold ${row.difference > 0 ? 'text-amber-700' : row.difference < 0 ? 'text-emerald-700' : 'text-gray-600'}`}>
                                            {row.difference > 0 ? '+' : ''}{formatBrl(row.difference)}
                                        </p>
                                        {row.commission_floor && row.difference > 0 && (
                                            <div className="mt-1.5 flex justify-end">
                                                <Pill tone="orange">uplift da trava</Pill>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        <div className="flex flex-wrap gap-1">
                                            {row.exception && <Pill tone="amber">Exceção Ploomes</Pill>}
                                            {row.commission_floor && <Pill tone="orange">Trava 1%</Pill>}
                                            {row.strike_points > 0 && (
                                                <Pill tone="rose">+{row.strike_points} strike</Pill>
                                            )}
                                            <Pill tone={row.status === 'open' ? 'sky' : 'gray'}>
                                                {row.status === 'open' ? 'Aberto' : 'Revisado'}
                                            </Pill>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {!isLoading && rows.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                                    Nenhum registro de auditoria encontrado.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {meta && (
                <TablePagination
                    meta={{
                        current_page: meta.current_page ?? filters.page,
                        last_page: meta.last_page ?? 1,
                        per_page: meta.per_page ?? filters.per_page,
                        total: meta.total ?? 0,
                    }}
                    onPageChange={(page) => setFilters((p) => ({ ...p, page }))}
                    onPerPageChange={(per_page) => setFilters((p) => ({ ...p, per_page, page: 1 }))}
                    perPageOptions={[...TABLE_PER_PAGE_OPTIONS]}
                    itemLabel="registro"
                />
            )}
        </div>
    );
}
